import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Modal, Popconfirm, Space, Switch, Table, Tag, Typography, message, Grid } from 'antd';
import ColumnsCardList from '../components/common/ColumnsCardList';
import { DeleteOutlined, EditOutlined, PlusOutlined, ShopOutlined } from '@ant-design/icons';
import { locationsApi } from '../utils/api';

const { Title, Paragraph, Text } = Typography;

/**
 * Administre les points de vente physiques de la boutique e-commerce active.
 * Cette page ne crée jamais de nouvelle boutique : elle ajoute uniquement un
 * emplacement POS, auquel les caissiers pourront ensuite être affectés.
 * @returns {JSX.Element} L'écran de gestion des points de vente.
 */
function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [error, setError] = useState(null);
  const [form] = Form.useForm();

  /** Charge les emplacements de la boutique sélectionnée après le montage. */
  useEffect(() => {
    loadLocations();
  }, []);

  /** Récupère la liste courante en conservant un message de récupération clair. */
  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await locationsApi.getAll();
      setLocations(response.data || []);
      setError(null);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Impossible de charger les points de vente. Réessayez dans un instant.');
    } finally {
      setLoading(false);
    }
  };

  /** Ouvre le formulaire prérempli pour modifier, ou vide pour créer un emplacement. */
  const openForm = (location = null) => {
    setEditingLocation(location);
    form.setFieldsValue(location || { name: '', address: '', phone: '', is_active: true });
    setModalOpen(true);
  };

  /** Ferme le formulaire et efface les données temporairement saisies. */
  const closeForm = () => {
    setModalOpen(false);
    setEditingLocation(null);
    form.resetFields();
  };

  /** Enregistre un nouveau point de vente ou les modifications de l'emplacement ouvert. */
  const saveLocation = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editingLocation) {
        await locationsApi.update(editingLocation.id, values);
        message.success('Point de vente mis à jour.');
      } else {
        await locationsApi.create(values);
        message.success('Point de vente créé. Son code POS est prêt à être utilisé.');
      }
      closeForm();
      await loadLocations();
    } catch (requestError) {
      if (requestError.errorFields) return;
      message.error(requestError.response?.data?.error || 'Impossible d’enregistrer le point de vente.');
    } finally {
      setSaving(false);
    }
  };

  /** Supprime uniquement un point de vente qui ne possède aucun caissier. */
  const removeLocation = async (locationId) => {
    try {
      setSaving(true);
      await locationsApi.delete(locationId);
      message.success('Point de vente supprimé.');
      await loadLocations();
    } catch (requestError) {
      message.error(requestError.response?.data?.error || 'Impossible de supprimer ce point de vente.');
    } finally {
      setSaving(false);
    }
  };

  // Mobile (< md) : cartes à la place du tableau ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;

  const columns = [
    {
      title: 'Point de vente', dataIndex: 'name', key: 'name',
      render: (name) => <Text strong>{name}</Text>
    },
    {
      title: 'Code POS', dataIndex: 'code', key: 'code',
      render: (code) => <Text code copyable={{ text: code }}>{code}</Text>
    },
    {
      title: 'Coordonnées', key: 'contact', responsive: ['md'],
      render: (_, location) => <span>{location.address || location.phone || '—'}</span>
    },
    {
      title: 'Statut', dataIndex: 'is_active', key: 'is_active',
      render: (isActive) => <Tag color={isActive ? 'success' : 'default'}>{isActive ? 'Actif' : 'Inactif'}</Tag>
    },
    {
      title: 'Actions', key: 'actions',
      render: (_, location) => (
        <Space size="small" wrap>
          <Button aria-label={`Modifier ${location.name}`} icon={<EditOutlined />} onClick={() => openForm(location)}>Modifier</Button>
          <Popconfirm
            title="Supprimer ce point de vente ?"
            description="Cette action est possible seulement s’il n’a aucun caissier."
            okText="Supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
            onConfirm={() => removeLocation(location.id)}
          >
            <Button danger aria-label={`Supprimer ${location.name}`} icon={<DeleteOutlined />}>Supprimer</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <main className="p-4 sm:p-6" aria-labelledby="locations-title">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-2xl">
          <Title id="locations-title" level={isMobile ? 3 : 2} className="!mb-2">Points de vente</Title>
          <Paragraph className="!mb-0 text-slate-600">Les caissiers, leurs codes PIN et les ventes POS sont organisés par point de vente. Votre boutique e-commerce active reste inchangée.</Paragraph>
        </div>
        <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => openForm()} className="min-h-11 w-full sm:w-auto">
          Ajouter un point de vente
        </Button>
      </div>

      {error && <Alert className="mb-4" type="error" showIcon message="Chargement impossible" description={error} action={<Button size="small" onClick={loadLocations}>Réessayer</Button>} />}

      <Card>
        {isMobile ? (
          /* Mobile : une carte par point de vente (nom en haut, actions Modifier/Supprimer en bas) */
          <ColumnsCardList
            columns={columns}
            dataSource={locations}
            loading={loading}
            headerKeys={['name']}
            actionKeys={['actions']}
            clientPageSize={10}
            emptyText="Aucun point de vente. Créez-en un pour commencer à utiliser le POS."
          />
        ) : (
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={locations}
          locale={{ emptyText: 'Aucun point de vente. Créez-en un pour commencer à utiliser le POS.' }}
          scroll={{ x: 720 }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
        />
        )}
      </Card>

      <Modal
        title={editingLocation ? 'Modifier le point de vente' : 'Ajouter un point de vente'}
        open={modalOpen}
        onCancel={closeForm}
        onOk={saveLocation}
        confirmLoading={saving}
        okText={editingLocation ? 'Enregistrer' : 'Créer le point de vente'}
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item name="name" label="Nom du point de vente" rules={[{ required: true, whitespace: true, message: 'Saisissez un nom pour ce point de vente.' }]}>
            <Input prefix={<ShopOutlined aria-hidden="true" />} autoFocus autoComplete="organization" placeholder="Ex. Plateau — Abidjan" />
          </Form.Item>
          <Form.Item name="address" label="Adresse">
            <Input autoComplete="street-address" placeholder="Adresse ou quartier (facultatif)" />
          </Form.Item>
          <Form.Item name="phone" label="Téléphone">
            <Input type="tel" autoComplete="tel" placeholder="Numéro de contact (facultatif)" />
          </Form.Item>
          <Form.Item name="is_active" label="Point de vente actif" valuePropName="checked">
            <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
          </Form.Item>
        </Form>
      </Modal>
    </main>
  );
}

export default Locations;
